import dynamic from 'next/dynamic';
import Image from 'next/image';

import WhiteLogo from 'assets/images/white-logo.svg';

import Button from 'molecules/Button';
import Icon from 'molecules/Icon';
import Link from 'molecules/Link';
import Section from 'molecules/Section';

import Menus from 'layout/footer/components/Menus';

import { hasArrayValues } from 'utils/array/arrays';

import type { FC } from 'react';
import type { LayoutProps } from 'types/global';

const CookiesButton = dynamic(() => import('layout/footer/components/CookiesButton'), { ssr: false });

const Footer: FC<FooterProps> = ({ newsletter, legal, menus, socials }) => (
  <footer className="footer-gradient-background py-12 sm:py-16 lg:py-20">
    <Section noBackground paddingBottom="none" paddingTop="none">
      <nav className="flex w-full flex-col gap-12 xl:flex-row xl:gap-32">
        <div className="flex flex-col gap-6 md:gap-20">
          <Link href="/" enablePreview={false}>
            <Image alt="Prismatic logo" src={WhiteLogo || ''} className="object-contain" width="200" />
          </Link>
          <div className="dark flex flex-col gap-3">
            <h2 className="font-spaceGrotesk text-xl font-semibold text-white">{newsletter.heading}</h2>
            <span className="font-roboto text-sm text-gray-300">{newsletter.description}</span>
            <Button label={newsletter.buttonLabel} href={newsletter.buttonLink} className="text-gray-900" />
          </div>
          <div>
            <div className="flex flex-row items-end gap-6 text-white">
              {socials.linkedIn && (
                <Link href={socials.linkedIn} aria-label="LinkedIn" enablePreview={false}>
                  <Icon icon="linkedin" />
                </Link>
              )}
              {socials.youtube && (
                <Link href={socials.youtube} aria-label="YouTube" enablePreview={false}>
                  <Icon icon="youtube" />
                </Link>
              )}
              {socials.github && (
                <Link href={socials.github} aria-label="GitHub" enablePreview={false}>
                  <Icon icon="github" />
                </Link>
              )}
            </div>
          </div>
        </div>
        {hasArrayValues(menus) && <Menus menus={menus} />}
      </nav>
      <div className="my-6 h-px w-full bg-white/50 opacity-10 lg:my-8" />
      <div className="flex w-full flex-col gap-8 md:flex-row md:justify-between">
        <div className="font-roboto text-sm text-white max-sm:text-center" aria-label="Footer copyright">
          © {new Date().getFullYear()} Prismatic. All rights reserved.
        </div>
        <div className="flex gap-6 max-md:w-full max-md:justify-between max-md:gap-3">
          {hasArrayValues(legal) &&
            legal.map(item => {
              if (!item || !item.label) {
                return;
              }

              if (item.label === 'Cookie Settings') {
                return <CookiesButton key={item.link} />;
              }

              return (
                <Link
                  key={item.link}
                  href={item.link}
                  className="w-fit px-0.5 font-roboto text-sm text-white hover:text-primary-100"
                  enablePreview={false}
                >
                  {item.label}
                </Link>
              );
            })}
        </div>
      </div>
    </Section>
  </footer>
);

/**
 * Renders a footer component using content from the Builder.io platform.
 *
 * This function retrieves the footer content from Builder.io and renders it using the `RenderBuilderContent` component.
 *
 * @returns {JSX.Element} The rendered footer component.
 */
export const FooterWithBuilder = ({ content }: LayoutProps) => <Footer {...content?.data} />;

export default Footer;
